import config from "../../config";

/**
 * Extracts the most specific error message from an error object
 * Handles different error response formats from APIs and network errors
 * Also handles simplified error objects from Axios interceptors
 *
 * @param {any} error - The error object received from catch block
 * @param {string} defaultMessage - Default message to use if no specific error is found
 * @param {boolean} addErrorPrefix - Whether to add "Error: " prefix to the extracted message
 * @returns {string} The most specific error message available
 */
export const extractErrorMessage = (
  error: any,
  defaultMessage = "An error occurred",
  addErrorPrefix = true
) => {
  let errorMessage = defaultMessage;
  const prefix = addErrorPrefix ? "Error: " : "";

  // Handle cases where error itself might be null or undefined
  if (!error) {
    return addErrorPrefix ? `${prefix}${errorMessage}` : errorMessage;
  }

  try {
    // CASE 1: Handle direct properties on the error object itself
    // This handles when the interceptor rejects with error.response.data or { error: errMsg }
    if (typeof error === "object") {
      // Check for direct error property (most common from interceptor)
      if (error.error && typeof error.error === "string") {
        errorMessage = error.error;
      }
      // Check for direct message property
      else if (error.message && typeof error.message === "string") {
        errorMessage = error.message;
      }
      // Check for data property (which might contain the actual error object)
      else if (error.data && typeof error.data === "object") {
        if (error.data.error && typeof error.data.error === "string") {
          errorMessage = error.data.error;
        } else if (
          error.data.message &&
          typeof error.data.message === "string"
        ) {
          errorMessage = error.data.message;
        }
      }
    } else if (typeof error === "string") {
      // Handle case where the error itself is a string
      errorMessage = error;
    }

    // CASE 2: Handle standard Axios error objects with response property
    if (error.response) {
      // Check for error info in response.data
      if (error.response.data) {
        if (typeof error.response.data === "string") {
          // If response data is a string, use it directly
          errorMessage = error.response.data;
        }
        // Handle case where response.data is an object containing an error property
        else if (typeof error.response.data === "object") {
          if (
            error.response.data.error &&
            typeof error.response.data.error === "string"
          ) {
            errorMessage = error.response.data.error;
          } else if (
            error.response.data.message &&
            typeof error.response.data.message === "string"
          ) {
            errorMessage = error.response.data.message;
          } else if (
            error.response.data.details &&
            typeof error.response.data.details === "string"
          ) {
            errorMessage = error.response.data.details;
          }
        }
      }

      // If we haven't found any specific message in data, try statusText
      if (errorMessage === defaultMessage && error.response.statusText) {
        errorMessage = error.response.statusText;
      }

      // If we still don't have a message, try adding status code context
      if (errorMessage === defaultMessage && error.response.status) {
        errorMessage = `Server returned ${error.response.status} error`;
      }
    }
    // CASE 3: Handle network errors (no response)
    else if (error.request && error.message) {
      // Network error where request was made but no response received
      errorMessage = error.message;
    }

    // Clean up the error message
    // Remove any "Error:" prefixes that might already exist to prevent duplication
    if (errorMessage.startsWith("Error:")) {
      errorMessage = errorMessage.substring(6).trim();
    }
  } catch (e) {
    // If anything goes wrong extracting the error, use the default message
    console.error("Error while extracting error message:", e);
  }

  return addErrorPrefix ? `${prefix}${errorMessage}` : errorMessage;
};

/**
 * Handles form submission errors by extracting the most specific error message
 * and combining it with the standard form error message format
 *
 * @param {any} error - The error object received from catch block
 * @param {string} formContext - The context of the form (e.g., "Submitting form", "Updating profile")
 * @returns {string} A formatted error message with support contact information
 */
export const handleFormError = (
  error: any,
  formContext = "Submitting form"
) => {
  // Extract the most specific error message from the error object
  const extractedMessage = extractErrorMessage(error, formContext, false);

  // Apply the standard form error label format which adds support contact info
  return postFormErrorLabel(extractedMessage);
};

/**
 * Logs an error from a form submission and returns a user-friendly error message
 *
 * @param {string} logContext - Context for logging (e.g., "ProfileForm.handleSubmit")
 * @param {any} error - The error object received from catch block
 * @param {string} formContext - The context of the form (e.g., "Submitting form", "Updating profile")
 * @param {Function} logFunction - Function to use for logging, defaults to console.error
 * @returns {string} A formatted error message with support contact information
 */
export const logAndFormatFormError = (
  logContext: any,
  error: any,
  formContext = "Submitting form",
  logFunction = console.error
) => {
  // Log the error with context
  logFunction(`${logContext} error:`, error);

  // Return a formatted error message
  return handleFormError(error, formContext);
};

const contactSupportLabel = `Please try again and/or contact support: ${config.emails.support}`;

export const postFormErrorLabel = (formLabel: string) => {
  return `${formLabel} Error: ${contactSupportLabel}`;
};

export const errorsToLabels = {
  userMustActivateAccount:
    "Activate your account using the token in your email titled 'Activate Account'",
  paymentInfoMissing: "Required payment information is missing.",
  failedToCancelSubscription: `Unable to cancel subscription: ${contactSupportLabel}`,
  failedToCreateNewSubscription: `Unable to create a new subscription: ${contactSupportLabel}`,
  failedToFetchPublicProfile: `Unable to fetch public profile: ${contactSupportLabel}`,
  failedToFetchPublicSchedule: `Unable to fetch public schedule: ${contactSupportLabel}`,
  failedToFetchRequiredUserData: `Unable to fetch required user data: ${contactSupportLabel}`,
  failedtoLoadBidAnalyticsData: `Unable to load bid analytics data: ${contactSupportLabel}`,
  failedToLoadBidsData: `Unable to load required bids data: ${contactSupportLabel}`,
  failedToLoadStreamsData: `Unable to load required streams data: ${contactSupportLabel}`,
  failedToLoadLiveFeedData: `Unable to load live feed (expired, inactive, or no payment): ${contactSupportLabel}`,
  failedToLoadPaymentsData: `Unable to load required payments data: ${contactSupportLabel}`,
  failedToLoadProductData: `Unable to load required product data: ${contactSupportLabel}`,
  failedToLoadPropertyData: `Unable to load required property data: ${contactSupportLabel}`,
  failedToPurchaseProduct: `Purchase failed: ${contactSupportLabel}`,
  failedToSignOut: `Unable to sign out: ${contactSupportLabel}`,
  failedToLoadRecommendations: `Unable to load recommendations: ${contactSupportLabel}`,
  failedToSubmitScheduledBid: `Unable to submit scheduled bid: ${contactSupportLabel}`,
  failedToSubmitSubscribedBid: `Unable to submit weekly subscribed message (free bid): ${contactSupportLabel}`,
  failedToLoadSubscriptionData: `Unable to load required subscription data: ${contactSupportLabel}`,
  failedToUpdateAccount: `Unable to update account information: ${contactSupportLabel}`,
  failedToUpdateAutoAcceptBids: `Unable to update 'Auto Accept Bids' flag: ${contactSupportLabel}`,
  failedToUpdateProfile: `Unable to update profile: ${contactSupportLabel}`,
  failedToUpdateProfileImage: `Unable to update profile image: ${contactSupportLabel}`,
  failedToUpdateSocialLinks: `Unable to update social link(s): ${contactSupportLabel}`,
  failedToUpdateSubscriptionProductPrice: `Unable to update subscription product price: ${contactSupportLabel}`,
  failedToOnboardToStripe: ` "Stripe connect creation (onboarding) failed. ${contactSupportLabel}`,
};
